import { useDerivedState } from '@st/react-util/use-derived-state'
import { useProcess, useSelect } from '~/store'
import { DynamicEmailTemplate, MessageTemplateSubjectVariant } from '@st/sdk'
import {
  Button,
  HighlightableTextArea,
  HighlightChip,
  HighlightMap,
  Tab,
  TabGroup
} from '@st/theme'
import { useMemo, useState } from 'react'
import {
  selOrganizationId,
  selOrganizationTemplateGroups,
  stOrganizationModule,
  TemplateGroup
} from './st-organization-module'
import { OrganizationSettingsScaffold } from './st-organization-settings-scaffold'

export function STOrganizationMessageTemplatesPage() {
  const organizationId = useSelect('stOrganization', selOrganizationId)

  // See {@link selTemplateGroups} for more information on why we need template groups
  const templateGroups = useSelect('stOrganization', selOrganizationTemplateGroups)
  const [selectedGroupLabel, setSelectedGroupLabel] = useState<string | null>(null)

  if (!organizationId || templateGroups.length == 0) {
    return null
  }

  const activeLabel = selectedGroupLabel ?? templateGroups[0].label
  const selectedGroup = templateGroups.find((g) => g.label == activeLabel) ?? templateGroups[0]

  return (
    <OrganizationSettingsScaffold title="Settings">
      <div className="flex w-[940px] flex-col gap-4">
        <h2 className="mb-5 text-xl text-gray-800">Templates</h2>

        <TabGroup>
          {templateGroups.map((templateGroup) => (
            <Tab
              className="w-40"
              key={templateGroup.label}
              selected={templateGroup.label === selectedGroup.label}
              onClick={() => setSelectedGroupLabel(templateGroup.label)}
            >
              {templateGroup.label}
            </Tab>
          ))}
        </TabGroup>
        <MessageTemplateSettings templateGroup={selectedGroup} />
      </div>
    </OrganizationSettingsScaffold>
  )
}

function MessageTemplateSettings({
  templateGroup
}: {
  templateGroup: TemplateGroup
}) {
  const stOrganization = useProcess('stOrganization')
  const template = templateGroup.templates[0]
  const subjectVariantOptions = template.subjectVariantOptions

  const [comment, setComment] = useDerivedState(template.comment)
  const [subjectVariant, setSubjectVariant] = useDerivedState(
    template.subjectVariant ?? subjectVariantOptions[0].key
  )

  function onSave() {
    const messageTemplateIds = templateGroup.templates.map((t) => t.id)
    stOrganization.send({
      type: 'saveMessageTemplates',
      messageTemplateIds,
      comment,
      subjectVariant: subjectVariant as MessageTemplateSubjectVariant
    })
  }

  return (
    <div className="mt-4 flex items-start gap-5">
      <div className="flex w-[600px] flex-col gap-6">
        <div className="flex flex-col gap-2">
          <label className="font-semibold">Subject</label>
          <select
            className="h-12 rounded-lg border-0 py-1.5 pl-4 pr-12 text-gray-900 ring-1 ring-inset ring-gray-200 focus:ring-2 focus:ring-indigo-600 disabled:opacity-60"
            value={subjectVariant}
            disabled={subjectVariantOptions.length <= 1}
            onChange={(e) => setSubjectVariant(e.target.value as typeof subjectVariant)}
          >
            {subjectVariantOptions.map((opt) => (
              <option key={opt.key} value={opt.key}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-2">
          <label className="font-semibold">Message</label>
          <TemplateCommentEditor template={template} value={comment} onChange={setComment} />
        </div>

        <Button className="self-end" variant="primary" onClick={onSave}>Save template</Button>
      </div>

      <TemplatePlaceholderLegend template={template} />
    </div>
  )
}

export function TemplateCommentEditor({
  template,
  value,
  onChange
}: {
  template: DynamicEmailTemplate
  value: string
  onChange: (value: string) => void
}) {
  const higlights: HighlightMap = useMemo(() => {
    return Object.fromEntries(template.placeholders.map((p) => ['$' + p.name, 'bg-yellow-200']))
  }, [template])

  return (
    <HighlightableTextArea
      placeholder="Send a message"
      value={value}
      onChange={onChange}
      highlights={higlights}
    />
  )
}

export function TemplatePlaceholderLegend({ template }: { template: DynamicEmailTemplate }) {
  return (
    <div className="flex w-[320px] flex-col gap-6 rounded-md p-5">
      {template.placeholders.map((placeholder) => (
        <TemplateVarInfo
          key={placeholder.name}
          name={'$' + placeholder.name}
          description={placeholder.description}
        />
      ))}
    </div>
  )
}

function TemplateVarInfo({ name, description }: { name: string; description: string }) {
  return (
    <div className="flex flex-col items-start gap-2">
      <div className="flex w-24 flex-row">
        <HighlightChip className="bg-yellow-200">{name}</HighlightChip>
      </div>
      <div className="flex-1 text-base text-gray-700">{description}</div>
    </div>
  )
}
